import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Autocomplete,
  Box,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { AccountBalance, Business, Person } from '@mui/icons-material';
import { getApiUrl } from '../config';
import { GlassButton, GlassTextField, GlassInfoCard } from './ui/GlassComponents';

// Glass styling for FormControl components
const glassFormControlSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.25)',
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.35)',
      },
    },
    '&.Mui-focused': {
      background: 'rgba(255, 255, 255, 0.08)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'rgba(255, 255, 255, 0.95)',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255, 255, 255, 0.6)',
  },
};

const TransferDialog = ({ 
  open, 
  onClose, 
  onTransfer, 
  availableCurrencies = [], 
  userDisplayName,
  sessionData,
  // New props for pre-filling
  prefillFromType = null,
  prefillFromId = null,
  prefillFromName = null,
  // Admin mode flag
  isAdminMode = false
}) => {
  const [transferData, setTransferData] = useState({
    currencySymbol: '',
    amount: '',
    fromType: '',
    fromId: '',
    fromName: '',
    toType: '',
    toId: '',
    toName: '',
    description: '',
    initiatedBy: userDisplayName || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data for dropdowns
  const [availableTreasuries, setAvailableTreasuries] = useState([]);
  const [availableBanks, setAvailableBanks] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [fromAccountSearchTerm, setFromAccountSearchTerm] = useState('');
  const [toAccountSearchTerm, setToAccountSearchTerm] = useState('');
  
  // UI state
  const [useCharacterName, setUseCharacterName] = useState(true);
  const [isFromLocked, setIsFromLocked] = useState(false);
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [balanceError, setBalanceError] = useState('');

  useEffect(() => {
    if (open) {
      // Check if we should lock the from fields
      const shouldLockFrom = !!(prefillFromType && prefillFromId);
      setIsFromLocked(shouldLockFrom);
      
      // Reset or pre-fill transfer data
      if (prefillFromType && prefillFromId) {
        setTransferData({
          currencySymbol: '',
          amount: '',
          fromType: prefillFromType,
          fromId: prefillFromId,
          fromName: prefillFromName || '',
          toType: '',
          toId: '',
          toName: '',
          description: '',
          initiatedBy: userDisplayName || ''
        });
        
        // If prefilling an account, also set the search term and search for it
        if (prefillFromType === 'account') {
          setFromAccountSearchTerm(prefillFromId);
          setUseCharacterName(false); // Use account number search for prefilled accounts
          // Search for the account to populate the options
          if (prefillFromId) {
            searchBankAccounts(prefillFromId);
          }
        }
      } else {
        // Reset form if no prefill data
        setTransferData({
          currencySymbol: '',
          amount: '',
          fromType: '',
          fromId: '',
          fromName: '',
          toType: '',
          toId: '',
          toName: '',
          description: '',
          initiatedBy: userDisplayName || ''
        });
      }
      
      // Load initial data
      loadTreasuries();
      loadBanks();
      
      // Reset search state
      setAccountSearchTerm('');
      setFromAccountSearchTerm('');
      setToAccountSearchTerm('');
      setAccountOptions([]);
      setUseCharacterName(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillFromType, prefillFromId, prefillFromName, userDisplayName]);

  useEffect(() => {
    // Search when from account search term changes
    if (fromAccountSearchTerm && fromAccountSearchTerm.length >= 2) {
      searchBankAccounts(fromAccountSearchTerm);
    } else if (!fromAccountSearchTerm) {
      setAccountOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromAccountSearchTerm]);

  useEffect(() => {
    // Search when to account search term changes
    if (useCharacterName && toAccountSearchTerm && toAccountSearchTerm.length >= 2) {
      searchBankAccounts(toAccountSearchTerm);
    } else if (!toAccountSearchTerm) {
      setAccountOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toAccountSearchTerm, useCharacterName]);

  useEffect(() => {
    // Fetch balance info when source or currency changes
    if (transferData.fromType && transferData.fromId && transferData.currencySymbol) {
      fetchBalanceInfo(transferData.fromType, transferData.fromId, transferData.currencySymbol);
    } else {
      setBalanceInfo(null);
      setBalanceError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferData.fromType, transferData.fromId, transferData.currencySymbol]);

  const getAuthToken = () => {
    return sessionData?.token || localStorage.getItem('token');
  };

  const loadTreasuries = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(getApiUrl('/treasuries'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableTreasuries(data.treasuries || []);
      } else {
        console.error('Failed to load treasuries:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Failed to load treasuries:', err);
    }
  };

  const loadBanks = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(getApiUrl('/banks'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableBanks(data.banks || []);
      } else {
        console.error('Failed to load banks:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Failed to load banks:', err);
    }
  };

  const searchBankAccounts = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setAccountOptions([]);
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        console.error('No auth token available for account search');
        return;
      }

      console.log('Searching accounts for:', searchTerm.trim());
      const response = await fetch(getApiUrl(`/accounts/search?q=${encodeURIComponent(searchTerm.trim())}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Account search results:', data);
        setAccountOptions(data.accounts || []);
      } else {
        console.error('Account search failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setAccountOptions([]);
      }
    } catch (err) {
      console.error('Failed to search accounts:', err);
      setAccountOptions([]);
    }
  };

  const fetchBalanceInfo = async (sourceType, sourceId, currencySymbol) => {
    if (!sourceType || !sourceId || !currencySymbol) {
      setBalanceInfo(null);
      setBalanceError('');
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        console.error('No auth token available');
        return;
      }

      const params = new URLSearchParams({
        sourceType,
        sourceId,
        currencySymbol
      });

      const response = await fetch(getApiUrl(`/transfers/balance?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBalanceInfo(data);
        setBalanceError('');
      } else {
        console.error('Failed to fetch balance:', response.status, response.statusText);
        setBalanceInfo(null);
        setBalanceError('Failed to fetch balance information');
      }
    } catch (err) {
      console.error('Failed to fetch balance:', err);
      setBalanceInfo(null);
      setBalanceError('Failed to fetch balance information');
    }
  };

  const handleInputChange = (field, value) => {
    setTransferData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAccountSelect = (account, isDestination = false) => {
    if (account) {
      const fieldPrefix = isDestination ? 'to' : 'from';
      setTransferData(prev => ({
        ...prev,
        [`${fieldPrefix}Type`]: 'account',
        [`${fieldPrefix}Id`]: account.accountNumber,
        [`${fieldPrefix}Name`]: `${account.bankName} - ${account.accountNumber} (${account.accountHolder})`
      }));
      
      // Update the appropriate search term
      const searchValue = account.isDefault ? account.accountHolder : account.accountNumber;
      if (isDestination) {
        setToAccountSearchTerm(searchValue);
      } else {
        setFromAccountSearchTerm(searchValue);
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'treasury': return <AccountBalance />;
      case 'bank': return <Business />;
      case 'account': return <Person />;
      default: return null;
    }
  };

  const renderAccountField = (isDestination = false, fieldLabel = "Account") => {
    const fieldPrefix = isDestination ? 'to' : 'from';
    const currentValue = transferData[`${fieldPrefix}Id`];
    const currentSearchTerm = isDestination ? toAccountSearchTerm : fromAccountSearchTerm;
    const setCurrentSearchTerm = isDestination ? setToAccountSearchTerm : setFromAccountSearchTerm;
    
    if (useCharacterName) {
      // Character name mode with autocomplete
      return (
        <Autocomplete
          options={accountOptions}
          getOptionLabel={(option) => 
            option.isDefault ? 
              `${option.accountHolder} (Default - ${option.bankName})` : 
              `${option.accountNumber} (${option.accountHolder} - ${option.bankName})`
          }
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Person sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2">
                    {option.isDefault ? `${option.accountHolder} (Default)` : option.accountNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.bankName} • {option.accountHolder}
                    {option.isDefault && ' • Default Account'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          value={accountOptions.find(opt => opt.accountNumber === currentValue) || null}
          onChange={(event, newValue) => handleAccountSelect(newValue, isDestination)}
          inputValue={currentSearchTerm}
          onInputChange={(event, newInputValue) => {
            setCurrentSearchTerm(newInputValue);
          }}
          renderInput={(params) => (
            <GlassTextField
              {...params}
              label={fieldLabel}
              margin="normal"
              required
              placeholder="Type character name..."
              size="small"
            />
          )}
          sx={{
            '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255, 255, 255, 0.6)' },
            '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.6)' },
          }}
          ListboxProps={{
            sx: {
              bgcolor: 'rgba(30, 30, 40, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '& .MuiAutocomplete-option': {
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                '&[aria-selected="true"]': {
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                },
              },
            },
          }}
          freeSolo={false}
          clearOnBlur={false}
          noOptionsText={accountSearchTerm.length < 2 ? "Type at least 2 characters" : "No accounts found"}
        />
      );
    } else {
      // Manual routing number mode
      return (
        <GlassTextField
          fullWidth
          label={fieldLabel}
          value={currentValue}
          onChange={(e) => handleInputChange(`${fieldPrefix}Id`, e.target.value)}
          margin="normal"
          required
          placeholder="Enter account/routing number..."
          size="small"
        />
      );
    }
  };

  const renderFromSelector = () => {
    switch (transferData.fromType) {
      case 'treasury':
        return (
          <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
            <InputLabel>From Treasury</InputLabel>
            <Select
              value={transferData.fromId}
              onChange={(e) => {
                const treasury = availableTreasuries.find(t => t.nationName === e.target.value);
                setTransferData(prev => ({
                  ...prev,
                  fromId: e.target.value,
                  fromName: treasury ? `${treasury.nationName} Treasury` : ''
                }));
              }}
              label="From Treasury"
              disabled={isFromLocked}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {availableTreasuries.map((treasury) => (
                <MenuItem key={treasury.nationName} value={treasury.nationName}>
                  {treasury.nationName} Treasury
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'bank':
        return (
          <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
            <InputLabel>From Bank Vault</InputLabel>
            <Select
              value={transferData.fromId}
              onChange={(e) => {
                const bank = availableBanks.find(b => b.id === e.target.value);
                setTransferData(prev => ({
                  ...prev,
                  fromId: e.target.value,
                  fromName: bank ? `${bank.name} Vault` : ''
                }));
              }}
              label="From Bank Vault"
              disabled={isFromLocked}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {availableBanks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} ({bank.jurisdictionNation})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'account':
        return renderAccountField(false, "From Account");

      default:
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, p: 2, textAlign: 'center' }}>
            Select a source type first
          </Typography>
        );
    }
  };

  const renderToSelector = () => {
    switch (transferData.toType) {
      case 'treasury':
        return (
          <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
            <InputLabel>To Treasury</InputLabel>
            <Select
              value={transferData.toId}
              onChange={(e) => {
                const treasury = availableTreasuries.find(t => t.nationName === e.target.value);
                setTransferData(prev => ({
                  ...prev,
                  toId: e.target.value,
                  toName: treasury ? `${treasury.nationName} Treasury` : ''
                }));
              }}
              label="To Treasury"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {availableTreasuries.map((treasury) => (
                <MenuItem key={treasury.nationName} value={treasury.nationName}>
                  {treasury.nationName} Treasury
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'bank':
        return (
          <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
            <InputLabel>To Bank Vault</InputLabel>
            <Select
              value={transferData.toId}
              onChange={(e) => {
                const bank = availableBanks.find(b => b.id === e.target.value);
                setTransferData(prev => ({
                  ...prev,
                  toId: e.target.value,
                  toName: bank ? `${bank.name} Vault` : ''
                }));
              }}
              label="To Bank Vault"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {availableBanks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} ({bank.jurisdictionNation})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'account':
        return (
          <Box>
            {renderAccountField(true, "To Account")}
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCharacterName}
                  onChange={(e) => {
                    setUseCharacterName(e.target.checked);
                    setAccountSearchTerm('');
                    setFromAccountSearchTerm('');
                    setToAccountSearchTerm('');
                    setAccountOptions([]);
                    handleInputChange('toId', '');
                  }}
                  size="small"
                />
              }
              label="Search by character name"
              sx={{ mt: 1 }}
            />
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, p: 2, textAlign: 'center' }}>
            Select a destination type first
          </Typography>
        );
    }
  };

  const handleSubmit = async () => {
    if (!transferData.currencySymbol || !transferData.amount || 
        !transferData.fromType || !transferData.fromId || 
        !transferData.toType || !transferData.toId) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(transferData.amount) <= 0) {
      setError('Transfer amount must be greater than 0');
      return;
    }

    // Validate balance (skip for admin mode)
    if (!isAdminMode && balanceInfo) {
      const transferAmount = parseFloat(transferData.amount);
      const availableBalance = balanceInfo.availableBalance;
      
      if (transferAmount > availableBalance) {
        if (balanceInfo.overdraftLimit > 0) {
          setError(`Insufficient funds. Available: ${balanceInfo.balance} ${transferData.currencySymbol} + ${balanceInfo.overdraftLimit} overdraft = ${availableBalance} ${transferData.currencySymbol}`);
        } else {
          setError(`Insufficient funds. Available: ${balanceInfo.balance} ${transferData.currencySymbol}`);
        }
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const transferPayload = {
        currencySymbol: transferData.currencySymbol,
        amount: parseInt(transferData.amount),
        fromType: transferData.fromType,
        fromId: transferData.fromId,
        toType: transferData.toType,
        toId: transferData.toId,
        description: transferData.description,
        initiatedBy: transferData.initiatedBy
      };

      console.log('Submitting transfer:', transferPayload);

      if (isAdminMode) {
        // Admin mode: call the onTransfer function directly (it's the admin transfer function)
        await onTransfer(transferPayload);
        handleClose();
      } else {
        // Regular mode: make API call to regular transfer endpoint
        const token = getAuthToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(getApiUrl('/transfers'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(transferPayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Transfer failed:', response.status, errorData);
          throw new Error(errorData.error || 'Transfer failed');
        }

        const result = await response.json();
        if (onTransfer) {
          onTransfer(result);
        }
        handleClose();
      }
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTransferData({
      currencySymbol: '',
      amount: '',
      fromType: '',
      fromId: '',
      fromName: '',
      toType: '',
      toId: '',
      toName: '',
      description: '',
      initiatedBy: userDisplayName || ''
    });
    setError('');
    setAccountSearchTerm('');
    setFromAccountSearchTerm('');
    setToAccountSearchTerm('');
    setAccountOptions([]);
    setUseCharacterName(true);
    setIsFromLocked(false);
    setBalanceInfo(null);
    setBalanceError('');
    onClose();
  };

  const getSourceDisplayName = () => {
    switch (transferData.fromType) {
      case 'treasury':
        return `${transferData.fromId} Treasury`;
      case 'bank':
        const bank = availableBanks.find(b => b.id === transferData.fromId);
        return bank ? `${bank.name} Vault` : transferData.fromId;
      case 'account':
        const account = accountOptions.find(acc => acc.accountNumber === transferData.fromId);
        return account ? `${account.accountNumber} (${account.accountHolder})` : transferData.fromId;
      default:
        return '';
    }
  };

  const getDestinationDisplayName = () => {
    switch (transferData.toType) {
      case 'treasury':
        return `${transferData.toId} Treasury`;
      case 'bank':
        const bank = availableBanks.find(b => b.id === transferData.toId);
        return bank ? `${bank.name} Vault` : transferData.toId;
      case 'account':
        const account = accountOptions.find(acc => acc.accountNumber === transferData.toId);
        return account ? `${account.accountNumber} (${account.accountHolder})` : transferData.toId;
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(30, 30, 40, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
          color: 'white',
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <DialogTitle sx={{
        color: 'white',
        fontWeight: 600,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        pb: 2
      }}>
        Transfer Funds
      </DialogTitle>
      <DialogContent sx={{ pt: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
        {error && (
          <GlassInfoCard severity="error">
            {error}
          </GlassInfoCard>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Source (From) */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600 }}>
              Source
            </Typography>
            <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={transferData.fromType}
                onChange={(e) => handleInputChange('fromType', e.target.value)}
                label="Source Type"
                disabled={isFromLocked}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 30, 40, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="treasury">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('treasury')}
                    Treasury
                  </Box>
                </MenuItem>
                <MenuItem value="bank">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('bank')}
                    Bank Vault
                  </Box>
                </MenuItem>
                <MenuItem value="account">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('account')}
                    Bank Account
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            {renderFromSelector()}
          </Grid>

          {/* Destination (To) */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600 }}>
              Destination
            </Typography>
            <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
              <InputLabel>Destination Type</InputLabel>
              <Select
                value={transferData.toType}
                onChange={(e) => handleInputChange('toType', e.target.value)}
                label="Destination Type"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 30, 40, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="treasury">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('treasury')}
                    Treasury
                  </Box>
                </MenuItem>
                <MenuItem value="bank">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('bank')}
                    Bank Vault
                  </Box>
                </MenuItem>
                <MenuItem value="account">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTypeIcon('account')}
                    Bank Account
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            {renderToSelector()}
          </Grid>

          {/* Currency and Amount */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" required size="small" sx={glassFormControlSx}>
              <InputLabel>Currency</InputLabel>
              <Select
                value={transferData.currencySymbol}
                onChange={(e) => handleInputChange('currencySymbol', e.target.value)}
                label="Currency"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 30, 40, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem key={currency.symbol} value={currency.symbol}>
                    {currency.symbol} - {currency.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <GlassTextField
              fullWidth
              label="Amount"
              type="number"
              value={transferData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              margin="normal"
              required
              inputProps={{ min: "0", step: "0.01" }}
              size="small"
            />
            {balanceInfo && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Available: {balanceInfo.balance} {transferData.currencySymbol}
                  {balanceInfo.overdraftLimit > 0 && (
                    <span> + {balanceInfo.overdraftLimit} overdraft</span>
                  )}
                </Typography>
                {transferData.amount && (
                  <Typography 
                    variant="body2" 
                    color={parseFloat(transferData.amount) > balanceInfo.availableBalance ? 'error' : 'success.main'}
                  >
                    Remaining: {balanceInfo.availableBalance - parseFloat(transferData.amount || 0)} {transferData.currencySymbol}
                  </Typography>
                )}
              </Box>
            )}
            {balanceError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {balanceError}
              </Typography>
            )}
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <GlassTextField
              fullWidth
              label="Description (optional)"
              value={transferData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              margin="normal"
              multiline
              rows={2}
              size="small"
            />
          </Grid>

          {/* Transfer Summary */}
          {transferData.fromType && transferData.toType && transferData.amount && (
            <Grid item xs={12}>
              <GlassInfoCard severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)', mb: 0.5 }}>
                  <strong>Transfer Summary:</strong> {transferData.amount} {transferData.currencySymbol}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)', mb: 0.5 }}>
                  From: {getTypeIcon(transferData.fromType)} {getSourceDisplayName()}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  To: {getTypeIcon(transferData.toType)} {getDestinationDisplayName()}
                </Typography>
              </GlassInfoCard>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        pt: 2,
        px: 3,
        pb: 2
      }}>
        <GlassButton variant="glass" onClick={handleClose} disabled={loading}>
          Cancel
        </GlassButton>
        <GlassButton
          variant="glass"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Transfer'}
        </GlassButton>
      </DialogActions>
    </Dialog>
  );
};

export default TransferDialog; 